import React, { FunctionComponent, useState, useCallback } from 'react';
import { useCss } from 'app/utils/css';
import { KeyPath } from 'app/models/entityStateSync/keypath';
import { ISize } from 'app/components/layout/useMeasure';
import { TraceType } from 'app/models/rtc/draw/trace';
import { useDraw } from 'app/components/rtc/draw/DrawContext';
import DrawInputSurface from 'app/components/rtc/draw/DrawInputSurface';
import DrawRender from 'app/components/rtc/draw/DrawRender';
import DrawToolbox from 'app/components/rtc/draw/DrawToolbox';

interface IProps {
  keyPath: KeyPath;
  size: ISize;
}

const Draw: FunctionComponent<IProps> = (props) => {
  const css = useCss();
  const [activeTool, toggleTool] = useDrawTool();

  const wrapClass = css('drawWrap', {
    position: 'relative',
    isolation: 'isolate',
    width: `${props.size.width}px`,
    height: `${props.size.height}px`,
  });

  return (
    <div className={wrapClass}>
      <DrawSurface {...props} activeTool={activeTool} />

      <div
        className={css('toolboxPosition', {
          margin: '12px',
          width: '32px',
        })}
      >
        <DrawToolbox activeTool={activeTool} setTool={toggleTool} />
      </div>
    </div>
  );
};

export const useDrawTool = (): [TraceType | null, (newValue: TraceType) => void] => {
  const [tool, setTool] = useState<TraceType | null>(null);

  const toggleTool = useCallback((newValue: TraceType) => {
    setTool((current) => newValue === current ? null : newValue);
  }, [setTool]);

  const { isBlocked } = useDraw();
  if (isBlocked) {
    return [null, toggleTool];
  }

  return [tool, toggleTool];
};

interface IDrawSurface {
  keyPath: KeyPath;
  size: ISize;
  activeTool: TraceType | null;
}

export const DrawSurface: FunctionComponent<IDrawSurface> = ({ activeTool, ...rest }) => {
  return <>
    <DrawRender {...rest} />
    {activeTool && <DrawInputSurface tool={activeTool} {...rest} />}
  </>;
};

export default Draw;
